import React from "react";
import { PlusIcon } from "@heroicons/react/16/solid";
import {
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  SquaresPlusIcon,
  AdjustmentsHorizontalIcon,
  LinkIcon,
  PencilSquareIcon,
  ChevronDoubleRightIcon,
  EyeIcon,
  ListBulletIcon,
  GlobeAltIcon,
  CalendarIcon,
  ArrowsUpDownIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { ComponentEnum, LessonI, ComponentI } from "../../../types";
import {
  SelectedComponentType,
  handleInsertComponent,
} from "../utils/selectedComponentUtils";
import { useNotification } from "../../../hooks/useNotification";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import Button from "../../../componentLibrary/Button";
import Accordion from "../../../componentLibrary/Accordion";
import { getNewComponentContent } from "../utils/addComponent";
import { COMPONENT_GROUPS, ComponentGroup } from "../utils/componentGroup";
import { PasteWhere } from "./PasteComponentModal";

const ComponentList = ({
  lesson,
  setLesson,
  currentPageId,
  selectedComponent,
  setSelectedComponent,
  handleFocusComponent,
}: {
  lesson: LessonI;
  setLesson: React.Dispatch<React.SetStateAction<LessonI>>;
  currentPageId: string;
  selectedComponent: SelectedComponentType;
  setSelectedComponent?: React.Dispatch<
    React.SetStateAction<SelectedComponentType>
  >;
  handleFocusComponent?: (component: ComponentI | null) => void;
}) => {
  const { notify } = useNotification();
  const componentIcons = {
    [ComponentEnum.TEXT]: DocumentTextIcon,
    [ComponentEnum.IMAGE]: PhotoIcon,
    [ComponentEnum.VIDEO]: VideoCameraIcon,
    [ComponentEnum.MULTIPLE_CHOICE]: ListBulletIcon,
    [ComponentEnum.RATING_SCALE]: AdjustmentsHorizontalIcon,
    [ComponentEnum.LESSON_LINK]: LinkIcon,
    [ComponentEnum.INPUT]: PencilSquareIcon,
    [ComponentEnum.LOGIC_COMPONENT]: EyeIcon,
    [ComponentEnum.TOGGLE_TEXT]: ChevronDoubleRightIcon,
    [ComponentEnum.USER_LIST]: SquaresPlusIcon,
    [ComponentEnum.CUSTOM_LINK]: GlobeAltIcon,
    [ComponentEnum.ACTION_DETAIL]: LinkIcon,
    [ComponentEnum.DATE_TIME]: CalendarIcon,
    [ComponentEnum.PRIORITY_LIST]: ArrowsUpDownIcon,
    [ComponentEnum.CALLOUT_BOX]: ChatBubbleLeftRightIcon,
    [ComponentEnum.ADD_TO_CALENDAR]: CalendarIcon,
  };

  const addNewComponent = (componentType: ComponentEnum) => {
    const content = getNewComponentContent(componentType);
    const newComponent = {
      _id: `${componentType}_${Date.now()}`,
      type: componentType,
      content,
    };
    handleInsertComponent(
      lesson,
      setLesson,
      currentPageId,
      newComponent,
      selectedComponent,
      pasteWhere,
      setSelectedComponent,
      handleFocusComponent,
      notify,
    );
  };

  const renderComponentItem = (type: ComponentEnum) => {
    const IconComponent = componentIcons[type] || DocumentTextIcon;
    return (
      <div
        key={type}
        className="flex justify-between py-1 px-2 items-center hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <IconComponent className="w-5 h-5" />
          <span className="text-sm">{type}</span>
        </div>
        <Button
          variant="dark"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            addNewComponent(type);
          }}
          className="mb-1"
        >
          <PlusIcon className="w-4 h-4" />
        </Button>
      </div>
    );
  };
  const [pasteWhere, setPasteWhere] = React.useState<PasteWhere>("Beginning");

  React.useEffect(() => {
    if (!selectedComponent.component) {
      setPasteWhere("Beginning");
    }
    if (
      selectedComponent.component?.type !== ComponentEnum.LOGIC_COMPONENT &&
      selectedComponent.component?.type !== ComponentEnum.FOR_EACH &&
      selectedComponent.component?.type !== ComponentEnum.TOGGLE &&
      pasteWhere === "Inside"
    ) {
      setPasteWhere("Below Selected Component");
    }
  }, [selectedComponent]);

  const options = [
    "Beginning",
    "End",
    ...(selectedComponent.component ? ["Below Selected Component"] : []),
    ...(selectedComponent.component?.type === ComponentEnum.LOGIC_COMPONENT ||
    selectedComponent.component?.type === ComponentEnum.FOR_EACH ||
    selectedComponent.component?.type === ComponentEnum.TOGGLE
      ? ["Inside"]
      : []),
  ];

  const quickAddComponent = (
    e: React.MouseEvent<HTMLButtonElement>,
    componentGroup: ComponentGroup,
  ) => {
    e.stopPropagation();
    addNewComponent(componentGroup.components[0]);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-2 mb-2">
        <Combobox
          label="Insert Component At"
          options={options.map((option) => ({
            label: option,
            value: option,
          }))}
          value={{ label: pasteWhere, value: pasteWhere } as ComboboxOptionI}
          onChange={(option) => {
            setPasteWhere(option.value as PasteWhere);
          }}
        />
      </div>
      {/* Desktop view */}
      <div className="hidden md:flex md:flex-col md:h-full md:min-h-0">
        <h3 className="mb-2">Component Library</h3>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {COMPONENT_GROUPS.map((group) => (
            <Accordion
              key={group.title}
              title={
                <div className="w-full flex flex-row items-center justify-between mr-4">
                  <span className="text-sm">{group.title}</span>
                  <Button
                    variant="dark"
                    size="sm"
                    onClick={(e) => quickAddComponent(e, group)}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
              }
              defaultOpen={group.title === "Input Group"}
              className="border-gray-300 w-full"
              headerClassName="w-full p-2 border-b border-gray-300"
            >
              <div className="space-y-1">
                {group.components.map((type) => renderComponentItem(type))}
              </div>
            </Accordion>
          ))}
        </div>
      </div>
      {/* Mobile view */}
      <div className="md:hidden">
        <div className="flex flex-wrap gap-1">
          {Object.values(ComponentEnum)
            .sort((a, b) => a.localeCompare(b))
            .map((type) => {
              const IconComponent = componentIcons[type] || DocumentTextIcon;
              return (
                <Button.Square
                  key={type}
                  variant="dark"
                  onClick={() => addNewComponent(type)}
                  className="p-1.5"
                  title={type}
                >
                  <IconComponent className="w-6 h-6" />
                </Button.Square>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default ComponentList;
